/* eslint-disable @typescript-eslint/no-unused-vars */
import * as React from 'react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';

interface ScheduleStatusBadgeProps {
	status: string;
	isRunning?: boolean;
	lastStatus?: {
		status: string;
		message?: string;
	};
	runHistory?: Array<{
		status: string;
		timestamp: string;
		error?: string;
	}>;
}

export function ScheduleStatusBadge({
	status,
	isRunning = false,
	lastStatus,
	runHistory,
}: ScheduleStatusBadgeProps) {
	const getStatusDetails = () => {
		// Debug logging for status determination
		console.log('🔍 ScheduleStatusBadge - Status Check:', {
			status,
			isRunning,
			lastStatus: lastStatus?.status,
			lastStatusMessage: lastStatus?.message,
		});

		// First check if status is PAUSED
		if (lastStatus?.status === 'PAUSED') {
			return {
				label: '⏸️ PAUSED',
				color: 'bg-neutral-500',
				message: lastStatus?.message,
			};
		}

		// 🔧 FIX: Show READY when completed and active but NOT currently running
		// This prevents showing "READY" when the schedule is actually running
		if (
			lastStatus?.status === 'COMPLETED' &&
			status === 'active' &&
			!isRunning
		) {
			return {
				label: '✅ READY',
				color: 'bg-green-600',
				message: 'Schedule is active and waiting for next run',
			};
		}

		// During task run - show detailed status updates
		const statusMap = {
			INITIALIZING: {
				label: '🚀 STARTING',
				color: 'bg-blue-500',
				message: lastStatus?.message,
			},
			RUNNING: {
				label: '🚀 RUNNING',
				color: 'bg-blue-500',
				message: lastStatus?.message,
			},
			SEARCHING: {
				label: '🔍 SEARCHING',
				color: 'bg-indigo-500',
				message: lastStatus?.message,
			},
			CHECKING_POSTS: {
				label: '🔍 CHECKING',
				color: 'bg-indigo-500',
				message: lastStatus?.message,
			},
			CHECKING_PROFILE: {
				label: '👤 CHECKING',
				color: 'bg-indigo-500',
				message: lastStatus?.message,
			},
			FETCHING_URLS: {
				label: '📑 FETCHING',
				color: 'bg-purple-500',
				message: lastStatus?.message,
			},
			GENERATING: {
				label: '✨ GENERATING',
				color: 'bg-purple-500',
				message: lastStatus?.message,
			},
			PUBLISHING: {
				label: '📤 PUBLISHING',
				color: 'bg-pink-500',
				message: lastStatus?.message,
			},
			WAITING: {
				label: '⏰ WAITING',
				color: 'bg-orange-500',
				message: lastStatus?.message,
			},
			SCRAPING: {
				label: '⚡️ IMPORTING',
				color: 'bg-pink-500',
				message: lastStatus?.message,
			},
			COMPLETED: {
				label: '✅ COMPLETED',
				color: 'bg-green-500',
				message: lastStatus?.message,
			},
			ERROR: {
				label: '❌ ERROR',
				color: 'bg-red-500',
				message: lastStatus?.message,
			},
		};

		// Show status from map if we have one
		if (
			lastStatus?.status &&
			statusMap[lastStatus.status as keyof typeof statusMap]
		) {
			return statusMap[lastStatus.status as keyof typeof statusMap];
		}

		// 🔧 FIX: Default to READY for active schedules that are NOT currently running
		// This prevents the flashing "READY" state during task execution
		if (status === 'active' && !isRunning) {
			return {
				label: '✅ READY',
				color: 'bg-green-600',
				message: 'Schedule is active and waiting for next run',
			};
		}

		// If running but no specific status, show generic running state
		if (isRunning) {
			return {
				label: '🚀 RUNNING',
				color: 'bg-blue-500',
				message: 'Task is currently running...',
			};
		}

		// Fallback
		return {
			label: '⏸️ INACTIVE',
			color: 'bg-neutral-500',
			message: 'Schedule is not active',
		};
	};

	const { label, color, message } = getStatusDetails();

	return (
		<div className='flex items-center gap-2 mb-4'>
			<span
				className={`px-3 py-1 rounded-full text-white font-semibold ${color}`}
			>
				{label}
			</span>
			<span className='text-sm text-muted-foreground'>{message}</span>
		</div>
	);
}
